import { useMemo, useRef } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { Image as ImageIcon, Info } from 'lucide-react';
import { PartStatus, PriceUnit, type Part, type PartImageInput } from '@ncc/shared';
import { api } from '../lib/api';
import { ImageManager } from './ImageManager';
import { PriceUnitSelect } from './PriceUnitSelect';

// Empty <select>/number inputs submit '' — turn that into null BEFORE coercion.
// Without this, z.coerce.number()('') === 0, which fails the API's
// `.positive().nullable()` id rules (→ 400) and stores $0 for blank prices.
const emptyToNull = (v: unknown) => (v === '' || v == null ? null : v);
const nullableId = z.preprocess(emptyToNull, z.coerce.number().int().positive().nullable());
const nullableMoney = z.preprocess(emptyToNull, z.coerce.number().nonnegative().nullable());
const nullableUnit = z.preprocess(emptyToNull, PriceUnit.nullable());

const FormSchema = z.object({
  part_number: z.string().min(1).max(100),
  sku: z.string().max(100).optional(),
  name: z.string().min(1).max(200),
  status: PartStatus,
  brand_id: nullableId,
  category_id: nullableId,
  regular_price: nullableMoney,
  sale_price: nullableMoney,
  price_unit: nullableUnit,
  fits: z.string().optional(),
  description: z.string().min(1),
});
export type PartFormValues = z.infer<typeof FormSchema>;

type Props = {
  initial?: Part;
  submitLabel: string;
  onSubmit: (values: PartFormValues, images: PartImageInput[]) => Promise<void>;
  initialImages?: PartImageInput[];
};

export function PartForm({ initial, submitLabel, onSubmit, initialImages }: Props) {
  const form = useForm<PartFormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: useMemo(() => toDefaults(initial), [initial]),
  });
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = form;

  const imagesRef = useRef<PartImageInput[]>(
    initialImages ??
      (initial?.images.map((i) => ({ url: i.url, image_id: i.image_id, position: i.position })) ??
        []),
  );

  const brandsQ = useQuery({ queryKey: ['brands'], queryFn: api.listBrands });
  const categoriesQ = useQuery({
    queryKey: ['part-categories'],
    queryFn: api.listPartCategories,
  });

  const submit: SubmitHandler<PartFormValues> = async (values) => {
    await onSubmit(values, imagesRef.current);
  };

  const errorOf = (key: keyof PartFormValues) =>
    errors[key] ? <div className="error-text">{errors[key]?.message as string}</div> : null;

  return (
    <form onSubmit={handleSubmit(submit)} noValidate>
      <h3 className="section-title">
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <ImageIcon size={14} /> Photos
        </span>
      </h3>
      <ImageManager
        initial={imagesRef.current as never}
        onChange={(next) => {
          imagesRef.current = next as PartImageInput[];
        }}
      />

      <h3 className="section-title">
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Info size={14} /> Part details
        </span>
      </h3>

      <div className="grid-form">
        <div className="field-span-4">
          <label>Part number *</label>
          <input placeholder="e.g. H-123-AB" {...register('part_number')} />
          {errorOf('part_number')}
        </div>
        <div className="field-span-4">
          <label>SKU</label>
          <input placeholder="Internal SKU (optional)" {...register('sku')} />
          {errorOf('sku')}
        </div>
        <div className="field-span-4">
          <label>Status</label>
          <select {...register('status')}>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="hidden">Hidden</option>
            <option value="out_of_stock">Out of stock</option>
          </select>
        </div>

        <div className="field-span-12">
          <label>Name *</label>
          <input placeholder="Customer-facing product name" {...register('name')} />
          {errorOf('name')}
        </div>

        <div className="field-span-4">
          <label>Brand</label>
          <select {...register('brand_id')}>
            <option value="">—</option>
            {brandsQ.data?.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field-span-4">
          <label>Category</label>
          <select {...register('category_id')}>
            <option value="">—</option>
            {categoriesQ.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field-span-3">
          <label>Regular price</label>
          <input type="number" step="0.01" placeholder="$" {...register('regular_price')} />
        </div>
        <div className="field-span-3">
          <label>Sale price</label>
          <input type="number" step="0.01" placeholder="$" {...register('sale_price')} />
        </div>
        <div className="field-span-3">
          <label>Price is per</label>
          <PriceUnitSelect register={register('price_unit')} />
        </div>

        <div className="field-span-12">
          <label>Fits</label>
          <textarea
            rows={3}
            placeholder="Free-text fitment notes — what trailers/equipment this part fits."
            {...register('fits')}
          />
        </div>

        <div className="field-span-12">
          <label>Description *</label>
          <textarea
            rows={6}
            placeholder="Sales-ready description that will appear on the public listing."
            {...register('description')}
          />
          {errorOf('description')}
        </div>
      </div>

      <div className="toolbar form-actions">
        <button className="primary" type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}

function toDefaults(initial?: Part): Partial<PartFormValues> {
  if (!initial) {
    return { status: 'draft' };
  }
  return {
    part_number: initial.part_number,
    sku: initial.sku ?? '',
    name: initial.name,
    status: initial.status,
    brand_id: initial.brand_id ?? null,
    category_id: initial.category_id ?? null,
    regular_price: initial.regular_price ?? undefined,
    sale_price: initial.sale_price ?? undefined,
    price_unit: initial.price_unit ?? null,
    fits: initial.fits ?? '',
    description: initial.description,
  };
}
